import dayjs from "dayjs";
import { CS_MESSAGE_CACHE } from "./Config";
import type { CsMessage } from "./SpaceCustomerServiceTypes";

const DT = "YYYY-MM-DD HH:mm:ss";

export interface CsMessageCacheScope {
  customerKey: string;
  spaceId: string;
}

export interface CsMessageCacheSnapshot {
  version: number;
  customerKey: string;
  spaceId: string;
  conversationId: string | null;
  messages: CsMessage[];
  maxMessageId: string | null;
  minMessageId: string | null;
  hasOlder: boolean;
  historyRecycled: boolean;
  updatedAt: string;
}

function cacheStorageKey(scope: CsMessageCacheScope): string {
  return `${CS_MESSAGE_CACHE.keyPrefix}.v${CS_MESSAGE_CACHE.version}:${scope.customerKey}:${scope.spaceId}`;
}

export function buildCsMessageCacheScope(
  spaceId: string,
  customerId?: string | null
): CsMessageCacheScope {
  const cid = customerId?.trim();
  return {
    customerKey: cid || "guest",
    spaceId: spaceId.trim(),
  };
}

function cmpMsgId(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function stripVolatileFields(m: CsMessage): CsMessage {
  const { localPreviewUrl: _p, imageSendStatus: _s, ...rest } = m;
  return rest;
}

export function mergeCsMessages(existing: CsMessage[], incoming: CsMessage[]): CsMessage[] {
  const map = new Map<string, CsMessage>();
  for (const m of existing) {
    if (m?.id) map.set(m.id, m);
  }
  for (const m of incoming) {
    if (m?.id) map.set(m.id, m);
  }
  return Array.from(map.values()).sort((a, b) => cmpMsgId(a.id, b.id));
}

export function trimCsMessagesForStorage(messages: CsMessage[]): CsMessage[] {
  const sorted = [...messages].sort((a, b) => cmpMsgId(a.id, b.id));
  if (sorted.length <= CS_MESSAGE_CACHE.maxItems) {
    return sorted.map(stripVolatileFields);
  }
  return sorted.slice(sorted.length - CS_MESSAGE_CACHE.maxItems).map(stripVolatileFields);
}

export function trimCsMessagesForDisplay(
  messages: CsMessage[],
  limit = CS_MESSAGE_CACHE.initialVisible
): CsMessage[] {
  const sorted = [...messages].sort((a, b) => cmpMsgId(a.id, b.id));
  if (sorted.length <= limit) {
    return sorted;
  }
  return sorted.slice(sorted.length - limit);
}

export function resolveCsMaxMessageId(messages: CsMessage[]): string | null {
  const real = messages.filter((m) => !m.id.startsWith("local-") && !m.imageSendStatus);
  return real.length ? real[real.length - 1].id : null;
}

export function loadCsMessageCache(scope: CsMessageCacheScope): CsMessageCacheSnapshot | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(cacheStorageKey(scope));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CsMessageCacheSnapshot;
    if (parsed.version !== CS_MESSAGE_CACHE.version) {
      localStorage.removeItem(cacheStorageKey(scope));
      return null;
    }
    if (parsed.customerKey !== scope.customerKey || parsed.spaceId !== scope.spaceId) {
      return null;
    }
    if (!Array.isArray(parsed.messages)) {
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(cacheStorageKey(scope));
    return null;
  }
}

export function saveCsMessageCache(
  scope: CsMessageCacheScope,
  input: Omit<CsMessageCacheSnapshot, "version" | "customerKey" | "spaceId" | "updatedAt">
): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const trimmed = trimCsMessagesForStorage(input.messages);
  const wasTrimmed = trimmed.length < input.messages.length;
  const snapshot: CsMessageCacheSnapshot = {
    version: CS_MESSAGE_CACHE.version,
    customerKey: scope.customerKey,
    spaceId: scope.spaceId,
    conversationId: input.conversationId,
    messages: trimmed,
    maxMessageId: input.maxMessageId,
    minMessageId: trimmed[0]?.id ?? null,
    hasOlder: input.hasOlder || wasTrimmed,
    historyRecycled: input.historyRecycled,
    updatedAt: dayjs().format(DT),
  };
  try {
    localStorage.setItem(cacheStorageKey(scope), JSON.stringify(snapshot));
  } catch {
    // ignore quota errors
  }
}

export function clearCsMessageCache(scope: CsMessageCacheScope): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(cacheStorageKey(scope));
}
